import { useEffect, type ReactNode } from "react";
import { useAuth } from "@clerk/clerk-react";
import { useProfile } from "@/hooks/use-profile";
import { setCurrencyDisplay, useCurrencyDisplay } from "@/lib/currency";

/**
 * Applies the user's display preferences app-wide:
 * - currency_display → drives the shared currency formatter (via the external
 *   store); subscribing here re-renders the subtree so money reformats live.
 * - compact_mode → toggles a `compact` class on <html> (smaller root font-size
 *   scales the rem-based layout down — real density change).
 *
 * default_view is consumed separately by the root redirect (RootRedirect).
 */
export function PreferencesProvider({ children }: { children: ReactNode }) {
  const { isSignedIn } = useAuth();
  const { data: me } = useProfile({ enabled: isSignedIn === true });
  // Subscribe so a currency change re-renders this subtree (non-memoized
  // children re-run formatCurrency and reformat immediately).
  useCurrencyDisplay();

  const currency = me?.profile.currency_display;
  const compact = me?.profile.compact_mode;

  useEffect(() => {
    if (currency) setCurrencyDisplay(currency);
  }, [currency]);

  useEffect(() => {
    document.documentElement.classList.toggle("compact", compact === true);
  }, [compact]);

  return <>{children}</>;
}
